import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Alert,
  Button,
  LinearProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import LoadingSpinner from './LoadingSpinner';
import FullScreenLoadingOverlay from './FullScreenLoadingOverlay';
import LanguageDropdown from './LanguageDropdown';
import i18n from '../i18n';
import './quickOnboarding/QuickOnboarding.scss';
import { checkAiAnalysisStatus } from '../services/instagramShowcaseService';

// Step components
import CombinedConnectUploadStep from './instagramShowcaseFlow/CombinedConnectUploadStep';
import AccountClaimStep from './instagramShowcaseFlow/AccountClaimStep';
import ProfileEditStep from './instagramShowcaseFlow/ProfileEditStep';
import ServiceCreationStep from './instagramShowcaseFlow/ServiceCreationStep';

interface InstagramPost {
  id: string;
  [key: string]: unknown;
}

interface FlowData {
  // Step 1 - Instagram Connection (session-based)
  instagramConnected: boolean;
  instagramToken: string | null;
  instagramUserId: string | null;
  instagramUsername: string | null;
  instagramPosts: InstagramPost[];

  // Step 2 - Showcase Upload & Bundle Creation
  selectedInstagramPosts: InstagramPost[];
  uploadedFiles: File[];
  instagramOnboardingToken: string | null;
  businessId: string | null;
  staffId: string | null;
  showcaseIds: string[];

  // Step 3 - Account Claim
  email: string;
  password: string;
  isAnalyzing: boolean;
  aiAnalysis: unknown;
  aiAnalysisComplete: boolean;
  aiAnalysisFailed: boolean;
  aiAnalysisError: string | null;
  aiAnalysisStatus: string;

  // Additional fields set dynamically
  canSkipClaimStep?: boolean;
  onboardingComplete?: boolean;
  redirectTo?: string | null;
  authToken?: string;
  user?: Record<string, unknown>;
  isAccountClaimed?: boolean;
  [key: string]: unknown;
}

interface StepDefinition {
  key: string;
  label: string;
  description: string;
}

/**
 * INSTAGRAM ONBOARDING FLOW - DATA STORAGE STRATEGY
 *
 * === STORAGE LOCATIONS ===
 * 1. localStorage.instagram_onboarding_token (JWT, 24h expiry)
 *    - Instagram OAuth JWT token
 *    - Used for API authentication during onboarding
 *    - Contains: { type: 'instagram_onboarding', staffId, businessId, instagramId }
 *
 * 2. In-memory flowData state (React useState)
 *    - NOT persisted to sessionStorage
 *    - Page refresh → always starts from step 0
 *
 * === AUTHENTICATION FLOW ===
 *
 * Step 1: User clicks "Connect Instagram"
 *   → Frontend calls /api/public/instagram/init-oauth
 *   → Backend redirects to Instagram OAuth page
 *   → User authorizes on Instagram
 *   → Instagram redirects back with authorization code
 *   → Backend exchanges code for access token
 *   → Backend creates/finds business & staff records
 *   → Backend generates JWT (instagram_onboarding_token)
 *   → Backend redirects to frontend with ?instagram_token=JWT
 *
 * Step 2: OAuth Callback Processing (CombinedConnectUploadStep)
 *   → Extract token from URL query param or hash
 *   → Store in localStorage (instagram_onboarding_token)
 *   → Call /connection-status API with Bearer token
 *   → Receive: { connected, instagramPosts, businessId, staffId, canSkipClaimStep }
 *   → Update flowData with full connection info (in-memory only)
 *   → Clean up URL (remove token from query params)
 *   → User can now select Instagram posts
 *
 * Step 3: Page Refresh Handling
 *   → Page refresh → flow resets to step 0
 *   → User must reconnect Instagram and go through flow again
 *   → This is intentional - prevents stale data issues
 *
 * Step 4: Token Validation & Error Handling
 *   → On ANY API call, check response status:
 *   → If 401 Unauthorized (invalid/expired token):
 *     → Clear localStorage.instagram_onboarding_token
 *     → Show "Session expired, please reconnect" message
 *   → If 404 Not Found (business/staff deleted):
 *     → Same as 401 - clear token and restart
 *   → If 500 or Network Error:
 *     → KEEP localStorage token
 *     → Show "Connection failed, please retry" message
 *
 * Step 5: Account Claim
 *   → User creates account with email/password
 *   → Backend generates full auth JWT (30 days)
 *   → Store in localStorage.token
 *
 * Step 6: Flow Completion
 *   → User completes all steps → navigates to dashboard
 *   → Keep localStorage.token (full auth token for app)
 *
 * === CRITICAL RULES ===
 * 1. Page refresh → always resets to step 0
 * 2. No state persistence - prevents stale data issues
 * 3. Only localStorage.instagram_onboarding_token persists (for API auth)
 */
const InstagramShowcaseFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Flow state - always starts at step 0 (no persistence)
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConnectingInstagram, setIsConnectingInstagram] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Language state
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || i18n.language;
  });

  // Initialize flowData - always starts fresh on page load (no persistence)
  const [flowData, setFlowData] = useState<FlowData>({
      // Step 1 - Instagram Connection (session-based)
      instagramConnected: false,
      instagramToken: null,
      instagramUserId: null,
      instagramUsername: null,
      instagramPosts: [],

      // Step 2 - Showcase Upload & Bundle Creation
      selectedInstagramPosts: [],
      uploadedFiles: [],
      instagramOnboardingToken: null, // JWT token from Instagram OAuth (24h expiry)
      businessId: null,
      staffId: null,
      showcaseIds: [],

      // Step 3 - Account Claim
      email: '',
      password: '',
      isAnalyzing: false,
      aiAnalysis: null,
      aiAnalysisComplete: false,
      aiAnalysisFailed: false,
      aiAnalysisError: null,
      aiAnalysisStatus: 'pending' // 'pending' | 'in_progress' | 'completed' | 'failed'

      // Step 4 - Profile Edit
      // Note: Profile data is fetched from database, not stored in flowData

      // Step 5 - Service Creation
      // Note: Services are created via API, not stored in flowData
    });

  const steps: StepDefinition[] = [
    {
      key: 'setup',
      label: t('instagramShowcase.steps.setup', 'Instagram & Showcase'),
      description: t('instagramShowcase.steps.setupDesc', 'Connect Instagram & create showcase')
    },
    {
      key: 'claim',
      label: t('instagramShowcase.steps.claim', 'Create Account'),
      description: t('instagramShowcase.steps.claimDesc', 'Claim your account')
    },
    {
      key: 'profile',
      label: t('instagramShowcase.steps.profile', 'Complete Profile'),
      description: t('instagramShowcase.steps.profileDesc', 'Finalize your details')
    },
    {
      key: 'services',
      label: t('instagramShowcase.steps.services', 'Create Services'),
      description: t('instagramShowcase.steps.servicesDesc', 'Set up your services')
    }
  ];

  // Utility function to scroll to top
  const scrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle step navigation
  const handleNext = async (): Promise<void> => {
    let nextStep = activeStep + 1;

    // Note: AI analysis blocking is now handled on ProfileEditStep itself
    // Users can navigate to ProfileEditStep, and the modal shows there if AI not complete

    // Skip AccountClaimStep (step 1) if account is already claimed
    if (activeStep === 0 && flowData.canSkipClaimStep) {

      // Auto-login the returning user
      try {
        setIsLoading(true);
        const { autoLoginClaimedAccount } = await import('../services/instagramShowcaseService');
        const loginData = await autoLoginClaimedAccount(flowData.instagramOnboardingToken!);

        if (loginData.success) {
          // Clear God Mode session state before new authentication
          sessionStorage.removeItem('impersonation_token');
          sessionStorage.removeItem('impersonated_user');
          sessionStorage.removeItem('godmode_original_user');
          sessionStorage.removeItem('godmode_original_token');

          // Store authentication token and user data
          if (loginData.token) localStorage.setItem('token', loginData.token);
          localStorage.setItem('user', JSON.stringify(loginData.user));

          // Update flow data with permanent authentication token
          updateFlowData({
            authToken: loginData.token, // Permanent auth token (30 days)
            user: loginData.user as Record<string, unknown>
          });

          // Check if onboarding is complete - if so, redirect directly to dashboard
          if (flowData.onboardingComplete) {
            toast.success(t('instagramShowcase.signIn.welcomeBack', 'Welcome back! Signed in with Instagram.'));
            setIsLoading(false);
            navigate('/dashboard', {
              state: {
                message: t('instagramShowcase.signIn.welcome', 'Welcome back!'),
                fromInstagramSignIn: true
              }
            });
            return; // Exit early - don't proceed to next step
          }

          toast.success(t('instagramShowcase.autoLogin.success', 'Welcome back! Logged in successfully.'));
        }
      } catch (error) {
        console.error('❌ Auto-login failed:', error);
        toast.error(t('instagramShowcase.autoLogin.error', 'Auto-login failed. Please try again.'));
      } finally {
        setIsLoading(false);
      }

      nextStep = 2; // Jump directly to ProfileEditStep
    }

    if (nextStep < steps.length) {
      setActiveStep(nextStep);
      scrollToTop();
    }
  };

  const handleBack = (): void => {
    let prevStep = activeStep - 1;

    // If on ProfileEditStep (step 2) and account was claimed, skip back to step 0
    if (activeStep === 2 && flowData.canSkipClaimStep) {
      prevStep = 0; // Jump back to CombinedConnectUploadStep
    }

    if (prevStep >= 0) {
      setActiveStep(prevStep);
      scrollToTop();
    }
  };

  const updateFlowData = (newData: Partial<FlowData>): void => {
    setFlowData(prev => ({ ...prev, ...newData }));
    // Note: flowData is not persisted - always starts fresh on page refresh
  };

  // AI Analysis Polling - Runs continuously across all steps when AI is analyzing
  useEffect(() => {
    // Only poll if we have the required data and AI is analyzing
    if (!flowData.staffId || !flowData.instagramOnboardingToken) {
      return;
    }

    if (flowData.aiAnalysisComplete || !flowData.isAnalyzing) {
      return;
    }


    // Timeout protection: 2.5 minutes
    const startTime = Date.now();
    const TIMEOUT_MS = 150000; // 2.5 minutes (150 seconds)

    const pollAiStatus = async (): Promise<void> => {
      // Check timeout
      if (Date.now() - startTime > TIMEOUT_MS) {
        updateFlowData({
          isAnalyzing: false,
          aiAnalysisFailed: true
        });
        toast.warning(t('instagramShowcase.upload.aiTimeout',
          'AI analysis is taking longer than expected. You can continue manually or retry.'));
        return;
      }

      try {
        const status = await checkAiAnalysisStatus(flowData.staffId!, flowData.instagramOnboardingToken!);

        // Backend returns: analysisComplete, analysisInProgress, analysisFailed, status
        if (status.analysisComplete) {
          updateFlowData({
            isAnalyzing: false,
            aiAnalysisComplete: true,
            aiAnalysis: status.hasAnalysisData,
            aiAnalysisStatus: status.status || 'completed'
          });
          toast.success(t('instagramShowcase.connect.aiComplete', 'AI analysis completed! Your profile has been enhanced.'));
        } else if (status.analysisFailed) {
          updateFlowData({
            isAnalyzing: false,
            aiAnalysisComplete: true,
            aiAnalysisError: status.lastError,
            aiAnalysisFailed: true,
            aiAnalysisStatus: status.status || 'failed'
          });
          toast.error(t('instagramShowcase.connect.aiFailed', 'AI analysis failed. You can continue manually.'));
        } else if (status.analysisInProgress) {
          const backendStatus = status.status || 'in_progress';
          // Update status even while analyzing to show real-time progress
          updateFlowData({
            aiAnalysisStatus: backendStatus
          });
        }
      } catch (error) {
        console.error('❌ Error polling AI analysis status:', error);
        // Don't fail the entire flow on polling errors, just log it
      }
    };

    // Poll immediately and then every 3 seconds
    pollAiStatus();
    const pollInterval = setInterval(pollAiStatus, 3000);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(pollInterval);
    };
  }, [flowData.staffId, flowData.instagramOnboardingToken, flowData.aiAnalysisComplete, flowData.isAnalyzing, t]);

  // Auto-redirect based on backend instruction (backend-driven routing)
  useEffect(() => {
    const checkBackendRedirect = async (): Promise<void> => {
      // Only check if:
      // 1. We're on step 0 (haven't progressed yet)
      // 2. Instagram is connected and verified
      // 3. Backend provided a redirectTo instruction
      // 4. redirectTo is not the current page (avoid redirect loop)
      // 5. Not currently loading

      if (
        activeStep === 0 &&
        flowData.instagramConnected === true &&
        flowData.redirectTo &&
        flowData.redirectTo !== '/instagram-onboarding' && // Don't redirect to self
        !isLoading
      ) {
        try {
          console.log('🔀 Backend redirect detected:', flowData.redirectTo);
          setIsLoading(true);

          // If backend says redirect to dashboard, we need to auto-login first
          if (flowData.redirectTo === '/dashboard' && flowData.canSkipClaimStep) {
            console.log('🔐 Auto-login before dashboard redirect...');

            // Auto-login to get permanent token
            const { autoLoginClaimedAccount } = await import('../services/instagramShowcaseService');
            const loginData = await autoLoginClaimedAccount(flowData.instagramOnboardingToken!);

            if (loginData.success) {
              console.log('✅ Auto-login successful, redirecting to dashboard');

              // Clear God Mode session state before new authentication
              sessionStorage.removeItem('impersonation_token');
              sessionStorage.removeItem('impersonated_user');
              sessionStorage.removeItem('godmode_original_user');
              sessionStorage.removeItem('godmode_original_token');

              // Store authentication token and user data
              if (loginData.token) localStorage.setItem('token', loginData.token);
              localStorage.setItem('user', JSON.stringify(loginData.user));

              // Show welcome message
              toast.success(t('instagramShowcase.signIn.welcomeBack', 'Welcome back! Signed in with Instagram.'));

              // Follow backend's redirect instruction (prefer auto-login response if provided)
              const finalRedirect = loginData.redirectTo || flowData.redirectTo;
              navigate(finalRedirect, {
                state: {
                  message: t('instagramShowcase.signIn.welcome', 'Welcome back!'),
                  fromInstagramSignIn: true
                }
              });

              // Note: No need to setIsLoading(false) since we're navigating away
            } else {
              throw new Error('Auto-login failed');
            }
          } else {
            // Other redirects (future extensibility)
            console.log('🔀 Navigating to:', flowData.redirectTo);
            navigate(flowData.redirectTo);
          }
        } catch (error) {
          console.error('❌ Auto-redirect failed:', error);
          toast.error(t('instagramShowcase.autoLogin.error', 'Auto-login failed. Please continue with the onboarding process.'));
          setIsLoading(false);
        }
      }
    };

    checkBackendRedirect();
  }, [
    activeStep,
    flowData.instagramConnected,
    flowData.redirectTo,
    flowData.canSkipClaimStep,
    flowData.instagramOnboardingToken,
    isLoading,
    navigate,
    t
  ]);

  // Note: Modal auto-hides when AI completes on ProfileEditStep
  // The condition (activeStep === 2 && !flowData.aiAnalysisComplete && flowData.isAnalyzing)
  // becomes false when flowData.aiAnalysisComplete changes to true

  // Active step is not persisted - always starts from step 0 on refresh

  // Instagram connection loading handler
  const setInstagramConnecting = (connecting: boolean): void => {
    setIsConnectingInstagram(connecting);
  };

  // Handle language change
  const handleLanguageChange = async (lng: string): Promise<void> => {
    try {
      setCurrentLanguage(lng);
      i18n.changeLanguage(lng);
      localStorage.setItem('preferredLanguage', lng);
      toast.success(t('instagramShowcase.languageChanged'));
    } catch (error) {
      console.error('Error changing language:', error);
      toast.error(t('instagramShowcase.errors.languageChangeFailed'));
    }
  };

  // Handle Instagram OAuth callback - Delegated to CombinedConnectUploadStep
  // The child component handles the full OAuth flow including:
  // - Token storage
  // - Connection verification
  // - Fetching posts
  // - Updating all flowData fields
  // This parent component no longer interferes with the callback

  // Handle completion
  const handleComplete = (): void => {
    // Navigate to dashboard
    navigate('/dashboard', {
      state: {
        message: t('instagramShowcase.success.message', 'Instagram showcase setup completed successfully!'),
        newUser: true
      }
    });
  };

  const renderStep = (): React.ReactNode => {
    switch (activeStep) {
      case 0:
        return (
          <CombinedConnectUploadStep
            flowData={flowData}
            onUpdate={updateFlowData}
            onNext={handleNext}
            onBack={handleBack}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setInstagramConnecting={setInstagramConnecting}
          />
        );
      case 1:
        return (
          <AccountClaimStep
            flowData={flowData}
            onUpdate={updateFlowData}
            onNext={handleNext}
            onBack={handleBack}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      case 2:
        return (
          <ProfileEditStep
            flowData={flowData}
            onUpdate={updateFlowData}
            onNext={handleNext}
            onBack={handleBack}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      case 3:
        return (
          <ServiceCreationStep
            flowData={flowData}
            onUpdate={updateFlowData}
            onComplete={handleComplete}
            onBack={handleBack}
            onError={setError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading && activeStep === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      className="instagram-onboarding-flow"
      sx={{
        minHeight: '100vh',
        py: 4,
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        px: { xs: 0, sm: 2 }
      }}
    >
      {/* AI Analysis Full-Screen Blocking Overlay - Shows on ProfileEditStep if AI not complete */}
      {activeStep === 2 && !flowData.aiAnalysisComplete && flowData.isAnalyzing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3
          }}
        >
          <Paper
            elevation={24}
            sx={{
              maxWidth: 500,
              width: '100%',
              p: 4,
              borderRadius: 3,
              textAlign: 'center'
            }}
          >
            {/* Debug Info - Remove after testing */}
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                Debug: isAnalyzing={String(flowData.isAnalyzing)}, failed={String(flowData.aiAnalysisFailed)}, status={flowData.aiAnalysisStatus}
              </Typography>
            )}

            {/* AI Analysis in Progress */}
            {flowData.isAnalyzing && !flowData.aiAnalysisFailed && (
              <>
                {/* Robot Animation */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      fontSize: 80,
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                        '50%': { transform: 'scale(1.1)', opacity: 0.8 }
                      }
                    }}
                  >
                    🤖
                  </Box>
                </Box>

                {/* Title */}
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('instagramShowcase.aiWaiting.title', 'Analyzing Your Instagram Profile')}
                </Typography>

                {/* Description */}
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {t('instagramShowcase.aiWaiting.analyzing',
                     'Our AI is analyzing your Instagram content to generate personalized service recommendations and profile suggestions tailored just for you.')}
                </Typography>

                {/* Real-time Status Display */}
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('instagramShowcase.aiWaiting.currentStatus', 'Current Progress:')}
                  </Typography>
                  <Typography variant="body2">
                    {flowData.aiAnalysisStatus === 'in_progress'
                      ? t('instagramShowcase.aiWaiting.statusAnalyzing', '⚙️ Analyzing your posts, identifying services, and creating suggestions...')
                      : flowData.aiAnalysisStatus === 'pending'
                        ? t('instagramShowcase.aiWaiting.statusPending', '⏳ Starting AI analysis engine...')
                        : t('instagramShowcase.aiWaiting.statusProcessing', '🔄 Processing your Instagram data...')
                    }
                  </Typography>
                </Alert>

                {/* Progress Bar */}
                <LinearProgress sx={{ mb: 2, height: 8, borderRadius: 4 }} />

                {/* Wait Time Message */}
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  ✨ {t('instagramShowcase.aiWaiting.wait',
                     'Almost there! This usually completes within 10-30 seconds.')}
                </Typography>
              </>
            )}

            {/* AI Analysis Failed */}
            {flowData.aiAnalysisFailed && (
              <>
                {/* Error Icon */}
                <Box sx={{ mb: 3, fontSize: 64 }}>⚠️</Box>

                {/* Title */}
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('instagramShowcase.aiWaiting.failed', 'AI Analysis Error')}
                </Typography>

                {/* Error Message */}
                <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    {flowData.aiAnalysisError || t('instagramShowcase.aiWaiting.failedDesc',
                      'The AI analysis encountered an error. You can retry or continue without AI suggestions.')}
                  </Typography>
                </Alert>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    onClick={() => {
                      updateFlowData({
                        isAnalyzing: true,
                        aiAnalysisFailed: false,
                        aiAnalysisComplete: false,
                        aiAnalysisStatus: 'pending'
                      });
                    }}
                    variant="outlined"
                    size="large"
                  >
                    {t('instagramShowcase.aiWaiting.retry', 'Retry AI Analysis')}
                  </Button>
                  <Button
                    onClick={() => {
                      updateFlowData({ aiAnalysisComplete: true, aiAnalysisFailed: false });
                      // Modal will hide automatically when aiAnalysisComplete becomes true
                    }}
                    variant="contained"
                    size="large"
                  >
                    {t('instagramShowcase.aiWaiting.continueWithout', 'Continue Without AI')}
                  </Button>
                </Box>
              </>
            )}

            {/* Fallback: AI Starting or Waiting State */}
            {!flowData.isAnalyzing && !flowData.aiAnalysisFailed && (
              <>
                {/* Loading Icon */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      fontSize: 80,
                      animation: 'rotate 2s linear infinite',
                      '@keyframes rotate': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  >
                    ⏳
                  </Box>
                </Box>

                {/* Title */}
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('instagramShowcase.aiWaiting.preparing', 'Preparing Your Profile Analysis')}
                </Typography>

                {/* Description */}
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {t('instagramShowcase.aiWaiting.preparingDesc',
                     'Our AI is getting ready to analyze your Instagram profile and create personalized recommendations for you.')}
                </Typography>

                {/* Info Box */}
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {t('instagramShowcase.aiWaiting.whatHappening', 'What\'s happening:')}
                  </Typography>
                  <Typography variant="body2">
                    • {t('instagramShowcase.aiWaiting.step1', 'Analyzing your Instagram posts and content')}<br/>
                    • {t('instagramShowcase.aiWaiting.step2', 'Identifying your service specialties')}<br/>
                    • {t('instagramShowcase.aiWaiting.step3', 'Creating personalized profile suggestions')}
                  </Typography>
                </Alert>

                {/* Progress Bar */}
                <LinearProgress sx={{ mb: 2, height: 8, borderRadius: 4 }} />

                {/* Wait Time Message */}
                <Typography variant="body2" color="text.secondary">
                  {t('instagramShowcase.aiWaiting.completingSoon', 'This will complete soon, usually within 10-30 seconds.')}
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      )}

      <Container
        maxWidth="md"
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          width: '100%',
          maxWidth: { xs: '100%', sm: '600px', md: '960px' }
        }}
      >
        <Paper
          elevation={3}
          className="content-paper"
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            }
          }}
        >
          {/* Language Dropdown */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <LanguageDropdown
              currentLanguage={currentLanguage}
              changeLanguage={handleLanguageChange}
            />
          </Box>

          {/* Header */}
          <Box className="header" sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              className="title"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              {t('instagramShowcase.title', 'Instagram Showcase Setup')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              className="subtitle"
              sx={{ mb: 3, maxWidth: '600px', mx: 'auto' }}
            >
              {t('instagramShowcase.subtitle', 'Connect your Instagram and create your beauty business profile in minutes')}
            </Typography>
          </Box>

          {/* Progress Stepper */}
          <Box className="stepper" sx={{ mb: 4 }}>
            <Stepper
              activeStep={activeStep}
              orientation={isMobile ? 'vertical' : 'horizontal'}
            >
              {steps.map((step) => (
                <Step key={step.key}>
                  <StepLabel>
                    <Typography variant="subtitle2" className="step-label" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" className="error-alert" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box>{renderStep()}</Box>
        </Paper>
      </Container>

      {/* Full Screen Loading Overlay for Instagram Connection */}
      <FullScreenLoadingOverlay
        isOpen={isConnectingInstagram}
        message={t('instagramShowcase.connecting.title', 'Connecting to Instagram...')}
        subMessage={t('instagramShowcase.connecting.description', 'Please wait while we redirect you to Instagram for authorization.')}
        showInstagramIcon={true}
      />
    </Box>
  );
};

export default InstagramShowcaseFlow;
